import { isEqual, pick } from 'es-toolkit';
import { action, computed, makeObservable, observable } from 'mobx';
import { DocumentGroupTypeCode, ProgramDocument, ProgramDocumentRequest } from '../../models';
import { programDocumentRequestSchema } from '../../models/schemas/program-document-request.schema';
import { createValidationState, ValidationState } from '../../shared/validation';

const COMPARABLE_FIELDS = [
  'displayName',
  'description',
  'documentGroupTypeCode',
  'winnerRole',
  'awardType',
  'ordinal',
] as const;

const AVAILABLE_CHARS_LENGTH = ['displayName', 'description'] as const;
type CharLengthFields = (typeof AVAILABLE_CHARS_LENGTH)[number];

export class UserResourcesPageUiStore {
  @observable programDocumentsByGroup = new Map<DocumentGroupTypeCode, ProgramDocument[]>();

  @observable programDocumentRequestBody?: ProgramDocumentRequest;

  @observable selectedFile?: File;

  @observable selectedProgramDocument?: ProgramDocument;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.programDocumentsByGroup.clear();
  }

  @action
  resetModalStates(): void {
    this.selectedFile = undefined;
    this.selectedProgramDocument = undefined;
    this.programDocumentRequestBody = undefined;
  }

  @action
  setProgramDocuments(programDocuments: ProgramDocument[]): void {
    this.programDocumentsByGroup.clear();

    programDocuments.forEach((document) => {
      const groupTypeCode = document.documentGroupTypeCode;
      const existingGroup = this.programDocumentsByGroup.get(groupTypeCode) || [];
      existingGroup.push(document);
      this.programDocumentsByGroup.set(groupTypeCode, existingGroup);
    });
  }

  @action
  setSelectedFile(file?: File): void {
    this.selectedFile = file;
  }

  @action
  setSelectedProgramDocument(programDocument?: ProgramDocument): void {
    this.selectedProgramDocument = programDocument;
  }

  @action
  createProgramDocumentRequestBody(): void {
    const initialValue: ProgramDocumentRequest = {
      displayName: '',
      description: '',
      documentGroupTypeCode: 'award-resource',
      winnerRole: undefined,
      awardType: 'Category',
      ordinal: this.currentProgramDocuments('award-resource').length + 1,
    };

    this.programDocumentRequestBody = initialValue;
  }

  @action
  createProgramDocumentRequestBodyForUpdate(existingProgramDocument: ProgramDocument): void {
    const initialValue: ProgramDocumentRequest = {
      programDocumentId: existingProgramDocument.programDocumentId,
      displayName: existingProgramDocument.displayName,
      fileLocationUrl: existingProgramDocument.fileLocationUrl,
      description: existingProgramDocument.description,
      documentGroupTypeCode: existingProgramDocument.documentGroupTypeCode,
      winnerRole: existingProgramDocument.winnerRole,
      awardType: existingProgramDocument.awardType,
      ordinal: existingProgramDocument.ordinal,
    };

    this.programDocumentRequestBody = initialValue;
  }

  @action
  setProgramDocumentRequestBody<T>(updatedField: { key: keyof ProgramDocumentRequest; value: T }) {
    if (!this.programDocumentRequestBody) return;

    const { key, value } = updatedField;
    const updatedProgramDocumentRequestBody = { ...this.programDocumentRequestBody, [key]: value };
    this.programDocumentRequestBody = updatedProgramDocumentRequestBody;
  }

  @computed
  get charsLength(): Record<CharLengthFields, number> {
    const defaultLengths: Record<CharLengthFields, number> = {
      displayName: 0,
      description: 0,
    };

    if (!this.programDocumentRequestBody) {
      return defaultLengths;
    }

    const requestBody = this.programDocumentRequestBody;

    return AVAILABLE_CHARS_LENGTH.reduce(
      (acc, fieldName) => {
        acc[fieldName] = requestBody[fieldName]?.length || 0;
        return acc;
      },
      { ...defaultLengths },
    );
  }

  @computed
  get programDocumentRequestValidation(): ValidationState<ProgramDocumentRequest> {
    return createValidationState(
      programDocumentRequestSchema.safeParse(this.programDocumentRequestBody),
    );
  }

  @computed
  get resourceFileErrorMessage(): string | undefined {
    return this.programDocumentRequestValidation.fieldErrors.fileLocationUrl?.join(' ')
      ?? (this.hasNeededFile ? undefined : 'Select a file.');
  }

  @computed
  get hasChanges(): boolean {
    if (!this.programDocumentRequestBody) return false;
    if (!this.selectedProgramDocument) return true;

    const originalFields = pick(this.selectedProgramDocument, COMPARABLE_FIELDS);
    const currentFields = pick(this.programDocumentRequestBody, COMPARABLE_FIELDS);

    const hasFieldsChanged = !isEqual(originalFields, currentFields);
    const hasNewFile = !!this.selectedFile;

    return (hasFieldsChanged || hasNewFile);
  }

  @computed
  get hasNeededFile(): boolean {
    if (!this.programDocumentRequestBody) return false;

    const hasExisting = !!this.programDocumentRequestBody.fileLocationUrl;
    const hasNew = !!this.selectedFile;

    return (hasExisting || hasNew);
  }

  @computed
  get isProgramDocumentRequestBodyValid(): boolean {
    return this.programDocumentRequestValidation.isValid && this.hasNeededFile && this.hasChanges;
  }

  @computed
  get currentProgramDocuments() {
    return (documentGroupTypeCode: DocumentGroupTypeCode): ProgramDocument[] => {
      return this.programDocumentsByGroup.get(documentGroupTypeCode) || [];
    }
  }
}
